import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * URL to POST the preview request to.
   * @type {String}
   */
  previewUrl: {
    type: String,
    required: true,
  },
  /**
   * URL to POST the confirmed assignment to.
   * @type {String}
   */
  processUrl: {
    type: String,
    required: true,
  },
  /**
   * The client's display name (e.g. "Advocate Health"). Used in the
   * client-control flag label and the outcome column text.
   * @type {String}
   */
  clientName: {
    type: String,
    default: "",
  },
  /**
   * JSON array of `{title, value}` option objects for the specialty
   * multi-select. Attributes arrive as strings, so this is parsed in the store.
   * @type {String}
   */
  specialties: {
    type: String,
    default: "[]",
  },
  /**
   * HTML content for the dropzone instruction text.
   * @type {String}
   */
  dropzoneText: {
    type: String,
    default: "Drag and drop spreadsheet here, or <strong>click to browse</strong>",
  },
  /**
   * Server-rendered HTML for the file specification panel.
   * @type {String}
   */
  fileSpecHtml: {
    type: String,
    default: "",
  },
  /**
   * Label for the term tag input field.
   * @type {String}
   */
  termInputLabel: {
    type: String,
    default: "Terms (separated by |)",
  },
  /**
   * Label for the "add" assignment mode option.
   * @type {String}
   */
  modeAddLabel: {
    type: String,
    default: "Add to existing specialties",
  },
  /**
   * Label for the "replace" assignment mode option.
   * @type {String}
   */
  modeReplaceLabel: {
    type: String,
    default: "Replace existing specialties",
  },
  /**
   * Label for the assignment mode select.
   * @type {String}
   */
  modeLabel: {
    type: String,
    default: "For existing terms",
  },
  /**
   * Label for the specialty multi-select.
   * @type {String}
   */
  specialtySelectLabel: {
    type: String,
    default: "Specialties to assign",
  },
  /**
   * Label for the client-control flag toggle. The `{client}` token is
   * substituted with `clientName`.
   * @type {String}
   */
  flagToggleLabel: {
    type: String,
    default: "Keep specialties under {client} control",
  },
  /**
   * Whether this deployment manages client-controlled specialties (canonical
   * MST management). The helper sets this as a bare present-or-absent
   * attribute. When falsy, the client-control toggle, the preview's Control
   * column, and the will-enable summary fragment are all omitted, and
   * `enable_client_specialties` is always submitted as "false".
   * @type {Boolean|String}
   */
  clientControlEnabled: booleanAttribute(),
  /**
   * JSON object of `{new, existing}` tooltip strings for the preview's row
   * status icons (server passes localized text). English defaults apply when
   * omitted. Error rows show the joined row errors instead.
   * @type {String}
   */
  statusLabels: {
    type: String,
    default: "",
  },
  /**
   * JSON object of `{will_enable, unchanged, new_term}` display strings for
   * the preview's Control column (server passes localized text). English
   * defaults using `clientName` apply when omitted.
   * @type {String}
   */
  outcomeLabels: {
    type: String,
    default: "",
  },
  /**
   * JSON object of localized UI copy, always passed by the helper. Deep-merged
   * over minimal English defaults in the store. Shape (camelCase keys):
   * `{preview, back, confirm, orDivider, termInputPlaceholder, invalidEntry,
   * removedSpecialty,
   * termsEntered: {one, other}, columns: {name, type, resultingSpecialties, control},
   * errors: {unsupportedFile, noTerms, noSpecialties, previewFailed, processFailed},
   * summary: {new, existing, willEnable, errors},
   * flash: {created, updated, flagEnabled, skipped}}`
   * — the summary/flash values are `{one, other}` templates with a literal
   * `%{count}` placeholder (willEnable/flagEnabled arrive with the client name
   * already interpolated); `removedSpecialty` uses `%{name}`.
   * @type {String}
   */
  labels: {
    type: String,
    default: "{}",
  },
  /**
   * Title for the modal.
   * @type {String}
   */
  modalTitle: {
    type: String,
    default: "Assign Specialties",
  },
  /**
   * Whether the modal should auto-open on mount.
   * @type {Boolean|String}
   */
  autoOpen: booleanAttribute(),
});
